/**
 * See the NOTICE file distributed with this work for additional information
 * regarding copyright ownership.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useEffect, useRef, type MutableRefObject } from 'react';

import analyticsTracking from 'src/services/analytics-service';

import { type AnalyticsOptions } from 'src/analyticsHelper';
import { useAppSelector } from 'src/store';
import { getCurrentApp } from '../globalSelectors';

const useGlobalAnalytics = <T extends HTMLElement>(
  ref: MutableRefObject<T | null>
) => {
  const appName = useAppSelector(getCurrentApp) || 'homepage';
  const appNameRef = useRef(appName);

  useEffect(() => {
    ref?.current?.addEventListener(
      'analytics',
      handleAnalyticsCustomEvent as EventListener
    );
    return () => {
      ref?.current?.removeEventListener(
        'analytics',
        handleAnalyticsCustomEvent as EventListener
      );
    };
  }, [ref?.current]);

  useEffect(() => {
    appNameRef.current = appName;
  }, [appName]);

  const sendTrackEvent = (payload: AnalyticsOptions) => {
    analyticsTracking.trackEvent({
      ...payload,
      app: payload.app ?? appNameRef.current
    });
  };

  const handleAnalyticsCustomEvent = (event: CustomEvent<AnalyticsOptions>) => {
    sendTrackEvent({
      ...event.detail
    });
  };
};

export default useGlobalAnalytics;
